import { useMemo, useRef } from "react";
import { useLocationContext } from "@/contexts/LocationContext";
import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { indexOrganizationPatients } from "src-next/modules/clinical-insights/services/insightPatientCache";

const EMPTY_PATIENTS_BY_ID: ReadonlyMap<string, fhir5.Patient> = new Map();

/**
 * Lê a lista de atendidos que o contexto já carregou.
 * Não chama consentimento nem Person: consentimento só entra se já estiver em memória.
 * `ready` fica falso até as locations e a lista de atendidos terminarem,
 * inclusive no primeiro frame em que `locationLoading` ainda é falso.
 */
export function useInsightOrganizationPatients() {
  const { access } = useSessionContext();
  const { location, locationLoading } = useLocationContext();
  const { patients, patientOrganizationLoading, consentPatients } =
    usePatientOrganizationContext();

  const accessType = access?.type;
  const isProfessional = accessType === "professional";
  const locationSettledRef = useRef(false);
  if ((accessType && !isProfessional) || locationLoading || location !== undefined) {
    locationSettledRef.current = true;
  }

  const ready =
    Boolean(accessType) &&
    (!isProfessional || (locationSettledRef.current && !locationLoading)) &&
    !patientOrganizationLoading;

  const patientsById = useMemo(() => {
    if (!ready) return EMPTY_PATIENTS_BY_ID;
    return indexOrganizationPatients(patients, consentPatients);
  }, [ready, patients, consentPatients]);

  return { ready, patientsById };
}
